/**
 * Time Tracking Module for Pinpoint Web App
 *
 * Tracks research time per case with activity detection.
 * Uses bridge client to access chrome.storage.local.
 */

import { bridge } from './bridge-client';

export interface TimeEntry {
  caseUrl: string;
  caseTitle: string;
  caseCitation: string;
  startTime: number;      // Unix timestamp
  duration: number;       // Milliseconds of active time
  lastActivity: number;   // Last activity timestamp
  dailyTime?: Record<string, number>;  // Time per day: { "2025-01-03": 3600000 }
}

export interface TimeStats {
  today: number;
  thisWeek: number;
  thisMonth: number;
  allTime: number;
  caseCount: number;        // Total cases tracked
  todayCases: number;       // Cases with time today
  thisWeekCases: number;    // Cases with time this week
  thisMonthCases: number;   // Cases with time this month
}

const STORAGE_KEY = 'timeEntries';
const INACTIVITY_THRESHOLD = 3 * 60 * 1000; // 3 minutes in milliseconds

/**
 * Get today's date as YYYY-MM-DD string (in local timezone)
 */
function getDateKey(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get all time entries from storage via bridge
 */
export async function getTimeEntries(): Promise<TimeEntry[]> {
  const result = await bridge.storageGet<{ [STORAGE_KEY]?: TimeEntry[] }>(STORAGE_KEY);
  return Array.isArray(result[STORAGE_KEY]) ? result[STORAGE_KEY] : [];
}

/**
 * Save time entries to storage via bridge
 */
async function saveTimeEntries(entries: TimeEntry[]): Promise<void> {
  await bridge.storageSet({ [STORAGE_KEY]: entries });
}

/**
 * Get or create time entry for a specific case
 */
export async function getOrCreateEntry(
  caseUrl: string,
  caseTitle: string,
  caseCitation: string
): Promise<TimeEntry> {
  const entries = await getTimeEntries();
  let entry = entries.find(e => e.caseUrl === caseUrl);

  if (!entry) {
    entry = {
      caseUrl,
      caseTitle,
      caseCitation,
      startTime: Date.now(),
      duration: 0,
      lastActivity: Date.now()
    };
    entries.push(entry);
    await saveTimeEntries(entries);
  }

  return entry;
}

/**
 * Update time entry with new activity
 */
export async function recordActivity(caseUrl: string): Promise<TimeEntry | null> {
  const entries = await getTimeEntries();
  const entry = entries.find(e => e.caseUrl === caseUrl);

  if (!entry) return null;

  const now = Date.now();
  const timeSinceLastActivity = now - entry.lastActivity;

  // Only count time if within activity threshold
  if (timeSinceLastActivity < INACTIVITY_THRESHOLD) {
    entry.duration += timeSinceLastActivity;

    // Also track daily time
    const todayKey = getDateKey();
    if (!entry.dailyTime) {
      entry.dailyTime = {};
    }
    entry.dailyTime[todayKey] = (entry.dailyTime[todayKey] || 0) + timeSinceLastActivity;
  }

  entry.lastActivity = now;
  await saveTimeEntries(entries);

  return entry;
}

/**
 * Check if currently active (had activity within threshold)
 */
export function isActive(entry: TimeEntry): boolean {
  return Date.now() - entry.lastActivity < INACTIVITY_THRESHOLD;
}

/**
 * Calculate time stats across all entries
 */
export async function getTimeStats(): Promise<TimeStats> {
  const entries = await getTimeEntries();

  // Get date boundaries
  const now = new Date();
  const todayKey = getDateKey();

  // Get all days in this week (Sunday to Saturday) using local dates
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  const weekDays: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    weekDays.push(getDateKey(d));
  }

  // Get all days in this month using local dates
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthDays: string[] = [];
  const tempDate = new Date(monthStart);
  while (tempDate.getMonth() === now.getMonth()) {
    monthDays.push(getDateKey(tempDate));
    tempDate.setDate(tempDate.getDate() + 1);
  }

  let today = 0;
  let thisWeek = 0;
  let thisMonth = 0;
  let allTime = 0;
  let todayCases = 0;
  let thisWeekCases = 0;
  let thisMonthCases = 0;

  // Date boundaries for period checks
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const weekStartDate = new Date(now);
  weekStartDate.setDate(now.getDate() - now.getDay());
  weekStartDate.setHours(0, 0, 0, 0);

  const monthStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
  monthStartDate.setHours(0, 0, 0, 0);

  for (const entry of entries) {
    allTime += entry.duration;

    let entryToday = 0;
    let entryWeek = 0;
    let entryMonth = 0;

    if (entry.dailyTime) {
      // Sum tracked daily time
      let trackedTotal = 0;
      for (const day of weekDays) {
        const dayTime = entry.dailyTime[day] || 0;
        trackedTotal += dayTime;
        entryWeek += dayTime;
        if (day === todayKey) {
          entryToday += dayTime;
        }
      }
      for (const day of monthDays) {
        const dayTime = entry.dailyTime[day] || 0;
        if (!weekDays.includes(day)) {
          trackedTotal += dayTime;
        }
        entryMonth += dayTime;
      }
      // Add any days outside this month to tracked total
      for (const day of Object.keys(entry.dailyTime)) {
        if (!monthDays.includes(day) && !weekDays.includes(day)) {
          trackedTotal += entry.dailyTime[day];
        }
      }

      // Handle unattributed time (duration accumulated before dailyTime tracking)
      const unattributed = entry.duration - trackedTotal;
      if (unattributed > 0) {
        // Attribute based on lastActivity - if active in period, add unattributed time
        if (entry.lastActivity >= todayStart.getTime()) {
          entryToday += unattributed;
        }
        if (entry.lastActivity >= weekStartDate.getTime()) {
          entryWeek += unattributed;
        }
        if (entry.lastActivity >= monthStartDate.getTime()) {
          entryMonth += unattributed;
        }
      }
    } else {
      // Legacy entries without dailyTime - attribute all to period if active then
      if (entry.lastActivity >= todayStart.getTime()) {
        entryToday = entry.duration;
      }
      if (entry.lastActivity >= weekStartDate.getTime()) {
        entryWeek = entry.duration;
      }
      if (entry.lastActivity >= monthStartDate.getTime()) {
        entryMonth = entry.duration;
      }
    }

    today += entryToday;
    thisWeek += entryWeek;
    thisMonth += entryMonth;

    // Count cases that contributed to each period
    if (entryToday > 0) todayCases++;
    if (entryWeek > 0) thisWeekCases++;
    if (entryMonth > 0) thisMonthCases++;
  }

  return { today, thisWeek, thisMonth, allTime, caseCount: entries.length, todayCases, thisWeekCases, thisMonthCases };
}

/**
 * Get time for a specific case
 */
export async function getCaseTime(caseUrl: string): Promise<number> {
  const entries = await getTimeEntries();
  const entry = entries.find(e => e.caseUrl === caseUrl);
  return entry?.duration || 0;
}

/**
 * Format milliseconds as HH:MM:SS or MM:SS
 */
export function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format milliseconds as human readable (e.g., "1h 23m")
 */
export function formatTimeHuman(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return '<1m';
  }
}

/**
 * Copy time entry as formatted text for billing
 */
export function formatTimeEntry(entry: TimeEntry): string {
  const duration = formatTimeHuman(entry.duration);
  return `${duration} - Research: ${entry.caseTitle}, ${entry.caseCitation}`;
}

/**
 * Reset time for a specific case
 * Returns the updated entry so caller can update state immediately
 */
export async function resetCaseTime(caseUrl: string): Promise<TimeEntry | null> {
  const entries = await getTimeEntries();
  const entry = entries.find(e => e.caseUrl === caseUrl);
  if (entry) {
    entry.duration = 0;
    entry.startTime = Date.now();
    entry.lastActivity = Date.now();
    entry.dailyTime = {}; // Clear daily tracking
    await saveTimeEntries(entries);
    return { ...entry }; // Return a copy of the updated entry
  }
  return null;
}
